import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Space,
  Table,
  Tag,
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { slugify } from 'utils/slugify';
import { LANGUAGE_TYPE_OPTIONS } from '../constants';
import { globalSettingService } from '../services/globalSettingService';
import { LanguageInterface } from '../types/globalSettingType';

interface GroupedLanguage {
  slug: string;
  name: string;
  rfc_code: string;
  order?: number;
  types: string[];
  languageRecords: LanguageInterface[];
}

const LanguageSection: React.FC = () => {
  const [languages, setLanguages] = useState<LanguageInterface[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [audioChecked, setAudioChecked] = useState(false);
  const [subtitleChecked, setSubtitleChecked] = useState(false);
  const [writeChecked, setWriteChecked] = useState(false);

  // Language data grouped by slug
  const groupedLanguages = useMemo<GroupedLanguage[]>(() => {
    const groupedMap = new Map<string, GroupedLanguage>();

    languages.forEach(lang => {
      if (!groupedMap.has(lang.slug)) {
        groupedMap.set(lang.slug, {
          slug: lang.slug,
          name: lang.name,
          rfc_code: lang.rfc_code,
          order: lang.order,
          types: [],
          languageRecords: [],
        });
      }

      const grouped = groupedMap.get(lang.slug)!;
      if (!grouped.types.includes(lang.type)) {
        grouped.types.push(lang.type);
      }
      grouped.languageRecords.push(lang);
    });

    return Array.from(groupedMap.values());
  }, [languages]);

  useEffect(() => {
    fetchLanguages();
  }, []);

  const fetchLanguages = async () => {
    try {
      setLoading(true);
      const response = await globalSettingService.fetchLanguages(
        new URLSearchParams({
          page_size: '100',
          order_by: 'order',
          order_direction: 'asc',
        })
      );
      setLanguages(response.results);
    } catch (error) {
      console.error('Failed to fetch languages:', error);
      Modal.error({ title: 'Error', content: 'Failed to load languages', okText: 'Confirm' });
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingSlug(null);
    setAudioChecked(false);
    setSubtitleChecked(false);
    setWriteChecked(false);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (groupedLang: GroupedLanguage) => {
    setEditingSlug(groupedLang.slug);
    setAudioChecked(groupedLang.types.includes('audio'));
    setSubtitleChecked(groupedLang.types.includes('subtitle'));
    setWriteChecked(groupedLang.types.includes('write'));
    form.setFieldsValue({
      name: groupedLang.name,
      rfc_code: groupedLang.rfc_code,
      slug: groupedLang.slug,
      order: groupedLang.order || 0,
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (slug: string) => {
    try {
      const groupedLang = groupedLanguages.find(g => g.slug === slug);
      if (!groupedLang) return;

      setLoading(true);
      // Delete all type records for the slug
      await Promise.all(
        groupedLang.languageRecords.map(lang => globalSettingService.deleteLanguage(lang.id))
      );
      message.success('Language deleted successfully');
      await fetchLanguages();
    } catch (error: any) {
      console.error('Failed to delete language:', error);
      Modal.error({ title: 'Error', content: error?.response?.data?.detail || 'Failed to delete language', okText: 'Confirm' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const selectedTypes: string[] = [];
      if (audioChecked) selectedTypes.push('audio');
      if (subtitleChecked) selectedTypes.push('subtitle');
      if (writeChecked) selectedTypes.push('write');

      if (selectedTypes.length === 0) {
        Modal.error({ title: 'Error', content: 'Please select at least one type', okText: 'Confirm' });
        return;
      }

      const slug = values.slug || slugify(values.name);

      setLoading(true);

      if (editingSlug) {
        // Get all records for the existing slug
        const existingGrouped = groupedLanguages.find(g => g.slug === editingSlug);
        if (!existingGrouped) {
          Modal.error({ title: 'Error', content: 'Language not found', okText: 'Confirm' });
          return;
        }

        // Update existing records matching selected types
        const typesToUpdate = existingGrouped.types.filter(t => selectedTypes.includes(t));
        const typesToCreate = selectedTypes.filter(t => !existingGrouped.types.includes(t));
        const typesToDelete = existingGrouped.types.filter(t => !selectedTypes.includes(t));

        // Update
        await Promise.all(
          typesToUpdate.map(type => {
            const existingRecord = existingGrouped.languageRecords.find(l => l.type === type);
            if (existingRecord) {
              return globalSettingService.updateLanguage(existingRecord.id, {
                name: values.name,
                rfc_code: values.rfc_code,
                slug: slug,
                type: type as 'audio' | 'subtitle' | 'write',
                order: values.order || 0,
              });
            }
          })
        );

        // Create
        await Promise.all(
          typesToCreate.map(type =>
            globalSettingService.createLanguage({
              name: values.name,
              rfc_code: values.rfc_code,
              slug: slug,
              type: type as 'audio' | 'subtitle' | 'write',
              order: values.order || 0,
            })
          )
        );

        // Delete
        await Promise.all(
          typesToDelete.map(type => {
            const existingRecord = existingGrouped.languageRecords.find(l => l.type === type);
            if (existingRecord) {
              return globalSettingService.deleteLanguage(existingRecord.id);
            }
          })
        );

        message.success('Language updated successfully');
      } else {
        // New creation: create record for each selected type
        await Promise.all(
          selectedTypes.map(type =>
            globalSettingService.createLanguage({
              name: values.name,
              rfc_code: values.rfc_code,
              slug: slug,
              type: type as 'audio' | 'subtitle' | 'write',
              order: values.order || 0,
            })
          )
        );
        message.success('Language created successfully');
      }

      setIsModalVisible(false);
      form.resetFields();
      setAudioChecked(false);
      setSubtitleChecked(false);
      setWriteChecked(false);
      setEditingSlug(null);
      await fetchLanguages();
    } catch (error: any) {
      console.error('Failed to save language:', error);
      Modal.error({ title: 'Error', content: error?.response?.data?.detail || 'Failed to save language', okText: 'Confirm' });
    } finally {
      setLoading(false);
    }
  };

  const getTypeDisplay = (type: string) => {
    return LANGUAGE_TYPE_OPTIONS.find(option => option.value === type)?.label || type;
  };

  const columns = [
    {
      title: 'Order',
      dataIndex: 'order',
      key: 'order',
      sorter: (a: GroupedLanguage, b: GroupedLanguage) => (a.order || 0) - (b.order || 0),
      width: 100,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
    },
    {
      title: 'RFC 5646',
      dataIndex: 'rfc_code',
      key: 'rfc_code',
    },
    {
      title: 'Types',
      dataIndex: 'types',
      key: 'types',
      render: (types: string[]) => (
        <Space wrap>
          {types.map(type => (
            <Tag key={type} color="blue">
              {getTypeDisplay(type)}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: GroupedLanguage) => (
        <Space>
          <Button size="small" onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.slug)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ marginBottom: '32px' }}>
      <Card
        title="Language"
        extra={
          <div style={{ fontSize: '12px', color: '#999', fontStyle: 'italic' }}>
            Used for subtitle language / Used for audio language
          </div>
        }
        style={{ border: '2px solid #d9d9d9' }}
        headStyle={{ borderBottom: '2px solid #d9d9d9' }}
      >
        <div style={{ marginBottom: '16px' }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Language
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={groupedLanguages}
          loading={loading}
          rowKey="slug"
          pagination={false}
        />

        <Modal
          title={editingSlug ? 'Edit Language' : 'Add Language'}
          open={isModalVisible}
          onOk={handleSubmit}
          onCancel={() => {
            setIsModalVisible(false);
            form.resetFields();
            setAudioChecked(false);
            setSubtitleChecked(false);
            setWriteChecked(false);
            setEditingSlug(null);
          }}
          confirmLoading={loading}
          width={600}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: 'Please enter language name' }]}
            >
              <Input placeholder="e.g., Korean" />
            </Form.Item>
            <Form.Item
              name="slug"
              label="Slug"
              rules={[{ required: true, message: 'Please enter slug' }]}
            >
              <Input placeholder="e.g., korean" />
            </Form.Item>
            <Form.Item
              name="rfc_code"
              label="RFC 5646"
              rules={[{ required: true, message: 'Please enter RFC 5646 code' }]}
            >
              <Input placeholder="e.g., ko" />
            </Form.Item>
            <Form.Item
              name="order"
              label="Order"
              rules={[{ type: 'number', min: 0, message: 'Order must be a non-negative number' }]}
            >
              <InputNumber min={0} placeholder="e.g., 0" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="Type" required>
              <Space direction="vertical">
                <Checkbox checked={audioChecked} onChange={e => setAudioChecked(e.target.checked)}>
                  {LANGUAGE_TYPE_OPTIONS.find(option => option.value === 'audio')?.label || ''}
                </Checkbox>
                <Checkbox
                  checked={subtitleChecked}
                  onChange={e => setSubtitleChecked(e.target.checked)}
                >
                  {LANGUAGE_TYPE_OPTIONS.find(option => option.value === 'subtitle')?.label || ''}
                </Checkbox>
                <Checkbox checked={writeChecked} onChange={e => setWriteChecked(e.target.checked)}>
                  {LANGUAGE_TYPE_OPTIONS.find(option => option.value === 'write')?.label || ''}
                </Checkbox>
              </Space>
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
                You can select multiple types. Each type will create a separate record with the same
                slug.
              </div>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default LanguageSection;
